import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck,
  faCheckSquare,
  faCoffee,
  faSpinner,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Breadcrumb, ButtonGroup, ButtonToolbar, Tabs, Tab } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAddActivityReportMutation, useGetReportsQuery } from '../services/portal';
import ReportService from '../services/report.service';
import ReportSidebar from '../components/ReportSidebar';
import { faHome } from '@fortawesome/free-solid-svg-icons';

library.add(faCheckSquare, faCoffee, faCheck, faSpinner, faChevronLeft, faChevronRight);

function getSessionStorageOrDefault(key, defaultValue) {
  const stored = sessionStorage.getItem(key);
  if (!stored) return defaultValue;
  return JSON.parse(stored);
}

const ReportView = () => {
  const { data: reports } = useGetReportsQuery();
  const {
    reportType,
    workspaceId,
    workspaceCode,
    reportId,
    reportCode,
    reportName
  } = useParams();

  const [isRendered, setIsRendered] = useState(false);
  const [token, setToken] = useState('');
  const [Roles, setRoles] = useState('');
  const [datasets, setDatasets] = useState('');
  const [Username, setUsername] = useState('');
  const [user] = useState(getSessionStorageOrDefault('user', null));
  const [show, setShow] = useState(false);
  const [report, setReport] = useState();
  const [exportCapturedBookmark, setExportCapturedBookmark] = useState(true);
  const [exportCurrentPage, setExportCurrentPage] = useState(false);
  const [exportFileformat, setExportFileformat] = useState();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('active'); // "active" | "disabled"

  const [addActivityReport] = useAddActivityReportMutation();

  useEffect(() => {
    setIsRendered(false);
    setReport(null);
    setToken('');
    setRoles('');
    setDatasets('');
    setUsername('');

    ReportService.getEmbedToken(
      workspaceId,
      workspaceCode,
      reportId,
      reportCode
    ).then(
      (response) => {
        setToken(response.data.accessToken);
        const rlsIdentity = response.data.rlsIdentity || {};
        if (response.data.datasetIds) setDatasets(response.data.datasetIds);
        if (rlsIdentity.roles) setRoles(rlsIdentity.roles);
        if (rlsIdentity.username) setUsername(rlsIdentity.username);
      },
      (error) => {
        alert(error.response.data.message);
        setToken(error.response.data.message);
      }
    );
  }, [workspaceId, workspaceCode, reportId, reportCode]);

  useEffect(() => {
    if (reportType === 'Paginated Report') logActivityReport('report_view');
  }, [reportType, reportId]);

  const logActivityReport = (activityType, comments = null) => {
    addActivityReport({
      user_id: user?.uid,
      organization_id: user?.oid,
      workspace_id: workspaceId,
      report_id: reportId,
      activity_type: activityType,
      comments
    }).unwrap().catch((error) => console.error('rejected', error));
  };

  const handleExportModalClose = () => setShow(false);
  const handleExportModalShow = () => setShow(true);
  const handleExportCapturedBookChange = (e) => setExportCapturedBookmark(e.target.value === 'true');
  const handleExportCurrentPageChange = (e) => setExportCurrentPage(e.target.checked);

  const getActivePage = async () => (await report.getPages()).find((page) => page.isActive);
  const getBookmarkActiveState = async () => report.bookmarksManager.capture({ allPages: true, personalizeVisuals: true });

  const handleDownloadFile = async () => {
    const exportFileDescription = exportFileformat?.toLowerCase() === 'pdf' ? 'PDF' : 'PowerPoint';
    let powerBIReportConfiguration = {};

    if (exportCapturedBookmark) {
      const bookmark = await getBookmarkActiveState();
      powerBIReportConfiguration.defaultBookmark = { state: bookmark.state };
      if (Roles) {
        powerBIReportConfiguration.identities = [{ roles: Roles, username: Username, datasets }];
      }
    }

    if (exportCurrentPage) {
      const activePage = await getActivePage();
      powerBIReportConfiguration.pages = [{ pageName: activePage.name }];
      if (Roles) {
        powerBIReportConfiguration.identities = [{ roles: Roles, username: Username, datasets }];
      }
    }

    setShow(false);

    const resolveReportExport = () => new Promise(async (resolve, reject) => {
      try {
        const response = await ReportService.getFileToDownload(
          workspaceCode,
          reportCode,
          exportFileformat,
          null,
          powerBIReportConfiguration
        );

        const today = new Date();
        const date = today.toISOString().split('T')[0].replace(/-/g, '');
        const time = today.toTimeString().split(' ')[0].replace(/:/g, '');
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `${reportName}_${date}_${time}.${exportFileformat}`);
        link.click();
        resolve();
      } catch (error) {
        if (error?.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);

            const parsedError = new Error(json.message || error.message);
            parsedError.response = error.response;
            reject(parsedError);
          } catch (parseError) {
            reject(error);
          }
        } else {
          reject(error);
        }
      }
    });

    toast.promise(resolveReportExport, {
      pending: {
        render: () => <div><strong>Exporting {exportFileDescription}...</strong></div>,
        icon: <FontAwesomeIcon icon="spinner" spin />
      },
      success: {
        render: () => <div><strong>{exportFileDescription} ready</strong></div>,
        icon: <FontAwesomeIcon icon="check" />
      },
      error: {
        render: ({ data }) => {
          const errorMessage = data?.message || 'Unknown error occurred';
          return <div><strong>Export failed:</strong> {errorMessage}</div>;
        }
      }
    });

    logActivityReport('report_export');
  };

  // ----- NEW: compute Active / Disabled sets like your example -----
  const allReports = reports?.reports ?? [];
  const activeReports = useMemo(
    () => allReports.filter(r => !!r.active).sort((a, b) => (a.report_name || '').localeCompare(b.report_name || '')),
    [allReports]
  );
  const disabledReports = useMemo(
    () => allReports.filter(r => !r.active).sort((a, b) => (a.report_name || '').localeCompare(b.report_name || '')),
    [allReports]
  );

  const currentList = sidebarTab === 'active' ? activeReports : disabledReports;

  return (
    <div className="d-flex" style={{ height:'calc(100vh - 3rem)', overflow: 'hidden', display: 'flex' }}>
      {/* Sidebar */}
      <div
        className="report-sidebar"
        style={{
          width: sidebarCollapsed ? '40px' : '280px',
          background: '#0a183a',
          borderRight: '1px solid #74E8C7',
          height: 'calc(100vh - 4rem)',
          overflowY: 'auto',
          zIndex: 1,
          position: 'relative'
        }}
      >
        {/* Collapse button (floating) */}
        <div
          style={{
            position: 'absolute',
            top: 40,
            right: sidebarCollapsed ? '0' : '10px',
            left: sidebarCollapsed ? '0' : 'unset',
            width: '40px',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 2,
            paddingTop: '10px'
          }}
        >
          <Button variant="light" size="sm" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <FontAwesomeIcon icon={sidebarCollapsed ? 'chevron-right' : 'chevron-left'} />
          </Button>
        </div>

        {/* Tabs header */}
        {!sidebarCollapsed && (
          <div style={{ marginTop: '60px', padding: '0 10px' }}>
            <Tabs
              id="sidebar-report-tabs"
              activeKey={sidebarTab}
              onSelect={(k) => setSidebarTab(k || 'active')}
              className="mb-2"
            >
              <Tab
                eventKey="active"
                title={`Active (${activeReports.length})`}
              />
              {/*<Tab
                eventKey="disabled"
                title={`Disabled (${disabledReports.length})`}
              />*/}
            </Tabs>
          </div>
        )}

        {/* List */}
        {!sidebarCollapsed && (
          <ReportSidebar
            reports={currentList}
            currentReportId={reportId}
            sidebarCollapsed={sidebarCollapsed}
          />
        )}
      </div>

      {/* Main content */}
      <div className="report-content flex-fill p-3" style={{ background: '#0a183a' }}>
        <header className="page-title-bar mb-3" style={{ paddingTop:'30px' }}>
          <ButtonToolbar className="justify-content-between">
            <Breadcrumb className="breadcrumb">
              <Breadcrumb.Item active>{reportName}</Breadcrumb.Item>
            </Breadcrumb>
            <ButtonGroup>
              {reportType === 'Report' && (
                <div className="dropdown" style={{ paddingTop:'30px' }}>
                  <button type="button" className="btn-export-custom" data-toggle="dropdown">
                    <i className="fas fa-file-export mr-1"></i> Export <i className="fa fa-caret-down mr-1"></i>
                  </button>
                  <div className="dropdown-menu">
                    <Link to="#" className="dropdown-item" onClick={() => { setExportFileformat('pdf'); handleExportModalShow(); }}>
                      <i className="fas fa-file-pdf mr-2"></i>PDF
                    </Link>
                    <Link to="#" className="dropdown-item" onClick={() => { setExportFileformat('pptx'); handleExportModalShow(); }}>
                      <i className="fas fa-file-powerpoint mr-2"></i>PowerPoint
                    </Link>
                  </div>
                </div>
              )}
            </ButtonGroup>
          </ButtonToolbar>
        </header>

        <div className="page-section">
          <div>
            <div name="RenderStyle" style={{ display: isRendered ? 'block' : 'none', height: 'calc(100vh - 12.5rem)' }}>
              <PowerBIEmbed
                embedConfig={{
                  type: 'report',
                  id: reportCode,
                  embedUrl: reportType === 'Report'
                    ? `https://app.powerbi.com/reportEmbed?groupId=${workspaceCode}&reportId=${reportCode}&actionBarEnabled=true`
                    : `https://app.powerbi.com/rdlEmbed?reportId=${reportCode}`,
                  accessToken: token,
                  tokenType: models.TokenType.Embed,
                  permissions: models.Permissions.ReadWrite,
                  viewMode: models.ViewMode.View,
                  settings: {
                    commands: [{ parameterPanel: { enabled: true, expanded: true } }],
                    panes: {
                      bookmarks: { visible: false },
                      fields: { expanded: false },
                      filters: { expanded: false, visible: true },
                      pageNavigation: { visible: true, position: models.PageNavigationPosition.Left },
                      selection: { visible: false },
                      syncSlicers: { visible: false },
                      visualizations: { expanded: false }
                    },
                    bars: {
                      actionBar: { visible: false },
                      statusBar: { visible: false }
                    },
                    navContentPaneEnabled: true,
                    hideErrors: true
                  }
                }}
                eventHandlers={new Map([
                  ['loaded', () => { logActivityReport('report_view'); setIsRendered(true); }],
                  ['rendered', () => { console.log('Report rendered'); }],
                  ['error', (event) => { logActivityReport('report_error', event?.detail?.message); }],
                  ['visualRendered', () => { console.log('Visual Rendered'); }],
                  ['visualClicked', () => { console.log('Visual Clicked'); }],
                  ['pageChanged', () => { console.log('Page Changed'); }]
                ])}
                cssClassName="embedded-report"
                getEmbeddedComponent={setReport}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      <Modal show={show} onHide={handleExportModalClose} animation={false} centered>
        <Modal.Header closeButton><Modal.Title>Export</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label htmlFor="export-report-option-select">Export with</label>
            <select
              className="custom-select"
              id="export-report-option-select"
              value={String(exportCapturedBookmark)}
              onChange={handleExportCapturedBookChange}
            >
              <option value="true">Current Values</option>
              <option value="false">Default Values</option>
            </select>
          </div>
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="export-only-current-page"
                checked={exportCurrentPage}
                onChange={handleExportCurrentPageChange}
              />
              <label className="custom-control-label" htmlFor="export-only-current-page">
                Only export current page
              </label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleDownloadFile}>Export</Button>
          <Button variant="secondary" onClick={handleExportModalClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReportView;
